import { useClientPortalAPI, useClientPortalSubmit } from "@certn/api";
import {
  Container,
  ExpandableCard,
  Grid,
  Layout,
  Loader,
  NeutralTag,
  SecondaryTopNav,
  SectionMessage,
  SuccessTag,
} from "@certn/cascadia-react";
import { PrimaryButton, showToast, Stack, Text } from "@certn/cascadia";
import { Formik } from "formik";
import { t } from "i18next";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useCanViewClientPortalPricing } from "../../../components/permissions/Permissions";
import { useAllowedCurrencyForSubsidiary } from "../../../hooks/useAllowedCurrencyForSubsidiary";
import { useClientAccount } from "../../../hooks/useClientAccount";
import { Schemas, Subsidiary } from "../../../types";
import { ActionsDropdown } from "../ActionsDropdown";
import { CriminalRecordTemplateFormFields } from "../components/CriminalRecordTemplateFormFields";
import {
  AUSTRALIAN_CRIMINAL_OPTION,
  CCRC_LEVEL,
  CRIMINAL_RECORD_VALIDATION,
  US_CRIMINAL_PREFERENCE,
} from "../constants";
import {
  useAustralianOrderingFlags,
  useCriminalRecordTemplatesInPackages,
} from "../hooks";
import {
  CriminalRecordTemplateFormCheckArguments,
  CriminalRecordTemplateFormValues,
  CriminalRecordTemplateUpdateRequest,
} from "../types";
import {
  australianOptionToCheckArguments,
  canUpdateCheckArguments,
  checkArgumentsToAustralianOption,
  checkArgumentsToUsPreference,
  cloneUsCriminalChecks,
  coerceCurrentAddressUsPreference,
  criminalRecordTemplateSchema,
  nameOnlyCriminalRecordTemplateSchema,
  usPreferenceToCheckArguments,
} from "../utils";

/**
 * GET `check_arguments` is an untyped JSON blob. Assert form nest + stored
 * AU/US API keys; the form keeps those radios at the root and maps them on submit.
 */
type CriminalRecordTemplateGetCheckArguments =
  CriminalRecordTemplateFormCheckArguments &
    Pick<
      Schemas["CriminalCheckArgumentsCreate"],
      | "australian_criminal_preference"
      | "australian_ncchc_check_type"
      | "us_criminal_preference"
      | "us_criminal_checks"
    >;

export function CriminalRecordTemplate() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { client } = useClientAccount();
  const canViewClientPortalPricing = useCanViewClientPortalPricing();
  const allowedCurrencies = useAllowedCurrencyForSubsidiary(
    client?.subsidiary as Subsidiary,
    true
  );
  const [selectedCurrency, setSelectedCurrency] = useState<string>(
    allowedCurrencies[0]?.id || "CAD"
  );
  const [enableMaximumPrice, setEnableMaximumPrice] = useState(false);
  const {
    clientPortalCriminalTemplatesEnhancements: criminalTemplatesEnhancements,
  } = useFlags();
  const australianOrderingFlags = useAustralianOrderingFlags(
    criminalTemplatesEnhancements
  );

  const templateIdsBelongingToActivePackages =
    useCriminalRecordTemplatesInPackages();

  const { data, mutate, isLoading, error } = useClientPortalAPI(
    `/api/internal/client-portal/check-arguments-templates/criminal-record/${templateId}/`
  );

  const { data: allTemplates } = useClientPortalAPI(
    `/api/internal/client-portal/check-arguments-templates/criminal-record/`
  );

  const activeTemplates = allTemplates?.results.filter(
    (template) => template.is_active === true
  );

  const { submit } = useClientPortalSubmit(
    `/api/internal/client-portal/check-arguments-templates/criminal-record/${templateId}/update/`,
    {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: async () => {
        await mutate();
        showToast(t("Criminal record template has been updated."), {
          type: "success",
        });
        navigate("/settings/criminal-record-templates");
      },
    }
  );

  const handleExitForm = () => {
    navigate("/settings/criminal-record-templates");
  };

  useEffect(() => {
    if (data?.check_arguments?.maximum_price) {
      const hasMaxPrice =
        Object.keys(data.check_arguments.maximum_price).length > 0;
      setEnableMaximumPrice(hasMaxPrice);
      if (hasMaxPrice) {
        const firstCurrency = Object.keys(
          data.check_arguments.maximum_price
        )[0];
        setSelectedCurrency(firstCurrency);
      }
    }
  }, [data]);

  if (isLoading) {
    return <Loader overlay />;
  }

  if (error) {
    return (
      <SectionMessage
        type="danger"
        sectionTitle={t("Unable to retrieve template.")}
        icon="error"
      >
        {error.message}
      </SectionMessage>
    );
  }

  if (!data) {
    return (
      <Stack align="center">
        <Text color="text-weak">
          {t("Unable to retrieve template information")}
        </Text>
      </Stack>
    );
  }

  const checkArguments = data.check_arguments as
    | CriminalRecordTemplateGetCheckArguments
    | null
    | undefined;
  const isInUse = Boolean(data.is_in_use);
  const canEditCheckArguments = canUpdateCheckArguments({
    criminalTemplatesEnhancements,
    isInUse,
  });
  const initialLookbackYears =
    (checkArguments?.umbrella_address_history_range_months ||
      CRIMINAL_RECORD_VALIDATION.DEFAULT_LOOKBACK_YEARS * 12) / 12;
  const loadedUsCriminalChecks = cloneUsCriminalChecks(
    checkArguments?.us_criminal_checks
  );
  const loadedUsCriminalPreference = checkArgumentsToUsPreference({
    us_criminal_preference: checkArguments?.us_criminal_preference,
    us_criminal_checks: loadedUsCriminalChecks,
  });
  const loadedUsSelection = {
    us_criminal_preference: loadedUsCriminalPreference,
    us_criminal_checks: loadedUsCriminalChecks,
  };
  const initialUsSelection =
    data.is_current_address && canEditCheckArguments
      ? coerceCurrentAddressUsPreference(
          loadedUsCriminalPreference,
          loadedUsCriminalChecks
        )
      : loadedUsSelection;
  const formCheckArguments: CriminalRecordTemplateFormCheckArguments | null =
    checkArguments
      ? {
          umbrella_minimum_address_duration_months:
            checkArguments.umbrella_minimum_address_duration_months,
          umbrella_address_history_range_months:
            checkArguments.umbrella_address_history_range_months,
          umbrella_apply_residency_rules_to_current_address:
            checkArguments.umbrella_apply_residency_rules_to_current_address,
          maximum_price: checkArguments.maximum_price,
          canadian_criminal_preference:
            checkArguments.canadian_criminal_preference,
        }
      : null;

  return (
    <Formik<CriminalRecordTemplateFormValues>
      initialValues={{
        name: data.name,
        lookback_years: initialLookbackYears,
        australian_criminal_option: checkArgumentsToAustralianOption({
          australian_criminal_preference:
            checkArguments?.australian_criminal_preference,
          australian_ncchc_check_type:
            checkArguments?.australian_ncchc_check_type,
        }),
        us_criminal_preference: initialUsSelection.us_criminal_preference,
        us_criminal_checks: initialUsSelection.us_criminal_checks,
        check_arguments: formCheckArguments,
        id: data.id,
        is_active: data.is_active,
        is_in_use: data.is_in_use,
        is_current_address: data.is_current_address,
      }}
      validationSchema={
        canEditCheckArguments
          ? criminalRecordTemplateSchema()
          : nameOnlyCriminalRecordTemplateSchema()
      }
      onSubmit={async (values, { setSubmitting }) => {
        try {
          const maxPrice = values.check_arguments?.maximum_price as
            | Record<string, number>
            | undefined;
          const maximum_price =
            enableMaximumPrice && maxPrice?.[selectedCurrency]
              ? { [selectedCurrency]: maxPrice[selectedCurrency] }
              : null;

          const payload: CriminalRecordTemplateUpdateRequest =
            canEditCheckArguments
              ? {
                  name: values.name,
                  check_arguments: {
                    umbrella_minimum_address_duration_months:
                      values.check_arguments
                        ?.umbrella_minimum_address_duration_months ||
                      CRIMINAL_RECORD_VALIDATION.DEFAULT_DURATION_MONTHS,
                    umbrella_address_history_range_months:
                      (values.lookback_years ||
                        CRIMINAL_RECORD_VALIDATION.DEFAULT_LOOKBACK_YEARS) * 12,
                    umbrella_apply_residency_rules_to_current_address:
                      values.check_arguments
                        ?.umbrella_apply_residency_rules_to_current_address ??
                      true,
                    canadian_criminal_preference:
                      values.check_arguments?.canadian_criminal_preference ||
                      CCRC_LEVEL.BASIC,
                    ...australianOptionToCheckArguments(
                      values.australian_criminal_option ??
                        AUSTRALIAN_CRIMINAL_OPTION.AFP
                    ),
                    ...usPreferenceToCheckArguments(
                      values.us_criminal_preference ??
                        US_CRIMINAL_PREFERENCE.NONE,
                      values.us_criminal_checks
                    ),
                    maximum_price,
                  },
                }
              : { name: values.name };
          await submit(payload);
        } catch (error) {
          console.error(error);
          setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, isSubmitting, setFieldValue, values }) => {
        return (
          <Layout
            layout="narrow"
            header={
              <SecondaryTopNav
                testID="close-button"
                leftIcon="close"
                onleftIconOnPress={handleExitForm}
              >
                <PrimaryButton
                  onPress={() => handleSubmit()}
                  isLoading={isSubmitting}
                >
                  {t("Save changes")}
                </PrimaryButton>
              </SecondaryTopNav>
            }
          >
            <Container>
              <Stack gap="space-3">
                <Text as="h1" style="global/title-medium">
                  {t("Criminal record template details")}
                </Text>
              </Stack>

              <Grid form>
                <ExpandableCard
                  title={
                    <Stack orientation="horizontal" gap="space-2">
                      {data.name}
                      {data.is_active ? (
                        <SuccessTag type="subtle">{t("Active")}</SuccessTag>
                      ) : (
                        <NeutralTag type="subtle">
                          {t("Deactivated")}
                        </NeutralTag>
                      )}
                    </Stack>
                  }
                  subtitle={t("Address-based")}
                  rightContent={
                    <ActionsDropdown
                      templateIdsBelongingToActivePackages={
                        templateIdsBelongingToActivePackages
                      }
                      templateId={data.id}
                      name={data.name}
                      isActive={data.is_active}
                      numActiveTemplates={activeTemplates?.length || 0}
                      mutate={mutate}
                      isCurrentAddress={data.is_current_address}
                      hideDropDownOnEditPage={data.is_current_address}
                    />
                  }
                />
                <CriminalRecordTemplateFormFields
                  isDisabled={!canEditCheckArguments}
                  showNameField={true}
                  values={values}
                  setFieldValue={setFieldValue}
                  allowedCurrencies={allowedCurrencies}
                  selectedCurrency={selectedCurrency}
                  setSelectedCurrency={setSelectedCurrency}
                  enableMaximumPrice={enableMaximumPrice}
                  setEnableMaximumPrice={setEnableMaximumPrice}
                  australianOrderingFlags={australianOrderingFlags}
                  showAustralianPreferences={criminalTemplatesEnhancements}
                  showUsPreferences={criminalTemplatesEnhancements}
                  isUsCatalogPricingVisible={canViewClientPortalPricing}
                />
              </Grid>
            </Container>
          </Layout>
        );
      }}
    </Formik>
  );
}
